<template>
  <!--
    Atlas content-suggestion review wizard. Renders inline inside
    Document_Holder's "Atlas" right-panel tab in place of the chat
    flyout while the user walks through suggested content for one or
    more document parts. Driven by the `document.suggest-content`
    page_action emitted by the agent's `suggest_part_content` tool;
    the host page (Document_Holder) registers the handler that hands
    us the suggestions array and listens for `@applied` per part.

    Why inline (and not a v-dialog modal):
      - The Atlas tab is the natural "the agent is doing a thing"
        surface in the right panel; staying inside it keeps the chat
        scrollback (and the rest of the doc layout) visible.
      - Avoids the v-dialog focus-trap interactions that already
        complicate the embedded AtlasFlyout.

    State model is per-suggestion:
      - `included` is a Set of item ids that the user has ticked
      - `finalText` is the editable assembled body, seeded from the
        ticked items but free for the user to edit further
      - `status` tracks the lifecycle: 'pending' → 'saving' → 'applied'
        (or 'skipped' / 'error'). Applied/skipped rows lock so the user
        can't accidentally re-apply on re-navigation.
  -->
  <div class="atlas-suggestion-review">
    <!--
      Chat-context band — the assistant turn that produced these
      suggestions. Renders above everything else so the user has a
      direct reminder of what they asked for and what the agent
      offered, instead of the panel popping up disconnected from
      the conversation. Hidden when no message text is available
      (defensive — every real trigger ships text, but a manual
      dispatch from the dev hook wouldn't).
    -->
    <div
      v-if="triggerMessage"
      class="atlas-suggestion-review__trigger"
    >
      <div class="atlas-suggestion-review__trigger-avatar">
        <AtlasLogo :size="18" variant="brand" />
      </div>
      <div class="atlas-suggestion-review__trigger-text">
        <MarkdownContent :text="triggerMessage" />
      </div>
    </div>

    <!-- Header strip — context + progress + close. -->
    <div class="atlas-suggestion-review__header">
      <button
        type="button"
        class="atlas-suggestion-review__nav-btn"
        :disabled="!canGoPrev"
        title="Previous suggestion"
        @click="goPrev"
      >
        <v-icon size="18">mdi-chevron-left</v-icon>
      </button>
      <div class="atlas-suggestion-review__progress">
        <span class="atlas-suggestion-review__progress-text">
          {{ currentIndex + 1 }} / {{ suggestions.length }}
        </span>
        <div class="atlas-suggestion-review__progress-dots">
          <span
            v-for="(s, i) in suggestionStates"
            :key="i"
            class="atlas-suggestion-review__dot"
            :class="[
              'atlas-suggestion-review__dot--' + s.status,
              { 'atlas-suggestion-review__dot--active': i === currentIndex },
            ]"
            :title="`Suggestion ${i + 1}: ${s.status}`"
          />
        </div>
      </div>
      <button
        type="button"
        class="atlas-suggestion-review__nav-btn"
        :disabled="!canGoNext"
        title="Next suggestion"
        @click="goNext"
      >
        <v-icon size="18">mdi-chevron-right</v-icon>
      </button>
      <button
        type="button"
        class="atlas-suggestion-review__close"
        title="Close review (discard remaining)"
        aria-label="Close review"
        @click="onCancel"
      >
        <v-icon size="20">mdi-close</v-icon>
      </button>
    </div>

    <!-- Scrollable body — current suggestion. -->
    <div class="atlas-suggestion-review__body">
      <div class="atlas-suggestion-review__part-label">
        {{ currentSuggestion.part_label || 'Document part' }}
      </div>

      <!--
        "Replace existing rows" opt-in. Shown only on NEW-row cards of a
        multi-row part that already has rows — lets the user turn an
        additive set of suggestions into a true replacement (e.g.
        "combine these 10 requirements into 6"). Off by default: deletion
        is destructive, so the user must opt in. Hidden once the row is
        locked. The host clears the rows (soft-delete, restorable) before
        creating the first replacement.
      -->
      <div
        v-if="currentReplaceTarget && !isCurrentLocked"
        class="atlas-suggestion-review__replace"
      >
        <label class="atlas-suggestion-review__replace-row">
          <input
            type="checkbox"
            :checked="isCurrentReplaceChecked"
            @change="setReplace(currentSuggestion.tp_id, $event.target.checked)"
          />
          <span>
            Delete the {{ currentReplaceTarget.count }} existing
            {{ currentReplaceTarget.label }}
            {{ currentReplaceTarget.count === 1 ? 'row' : 'rows' }}
            not in this set
          </span>
        </label>
        <div class="atlas-suggestion-review__replace-hint">
          This is a reduce/combine: the suggestions here are the complete
          new set, so the other existing rows are removed when you apply.
          Removed rows can be restored from the section's deleted-rows
          history.
        </div>
      </div>

      <!-- Rationale (collapsible, expanded by default the first time). -->
      <div v-if="currentSuggestion.rationale" class="atlas-suggestion-review__rationale">
        <button
          type="button"
          class="atlas-suggestion-review__section-toggle"
          :aria-expanded="rationaleOpen.toString()"
          @click="rationaleOpen = !rationaleOpen"
        >
          <v-icon size="14">
            {{ rationaleOpen ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
          </v-icon>
          Why this suggestion
        </button>
        <div v-if="rationaleOpen" class="atlas-suggestion-review__rationale-text">
          {{ currentSuggestion.rationale }}
        </div>
      </div>

      <!-- Current part content, read-only — for comparison. -->
      <div class="atlas-suggestion-review__section">
        <div class="atlas-suggestion-review__section-label">Current</div>
        <div class="atlas-suggestion-review__current">
          <template v-if="currentSuggestion.current_content">
            {{ currentSuggestion.current_content }}
          </template>
          <em v-else class="atlas-suggestion-review__empty">Empty</em>
        </div>
      </div>

      <!--
        Suggested items, when the suggestion arrived structured. Each
        row has a checkbox; toggling rebuilds `finalText` from the
        currently-ticked items (preserving any free-form edits the user
        already made would require diff logic we don't need yet —
        retoggling is destructive, the textarea below is the source of
        truth for the actual save).
      -->
      <div
        v-if="currentSuggestion.items && currentSuggestion.items.length > 0"
        class="atlas-suggestion-review__section"
      >
        <div class="atlas-suggestion-review__section-label">
          Pick items to include
        </div>
        <ul class="atlas-suggestion-review__items">
          <li
            v-for="item in currentSuggestion.items"
            :key="item.id"
            class="atlas-suggestion-review__item"
          >
            <label class="atlas-suggestion-review__item-row">
              <input
                type="checkbox"
                :checked="!!currentState.included[item.id]"
                :disabled="isCurrentLocked"
                @change="toggleItem(item.id, $event.target.checked)"
              />
              <span class="atlas-suggestion-review__item-text">{{ item.text }}</span>
            </label>
          </li>
        </ul>
      </div>

      <!--
        Final text — editable. Seeded from the ticked items (or the
        agent's free-form `suggested_content` when no items were
        returned), and is what gets sent on Apply. Disabled once the
        suggestion is applied/skipped so the user sees what was saved.
      -->
      <div class="atlas-suggestion-review__section">
        <div class="atlas-suggestion-review__section-label">
          Final text
          <span
            v-if="currentSuggestion.items && currentSuggestion.items.length > 0"
            class="atlas-suggestion-review__hint"
          >
            (edit freely — toggling items above rewrites this)
          </span>
        </div>
        <textarea
          v-model="currentState.finalText"
          class="atlas-suggestion-review__textarea"
          :disabled="isCurrentLocked"
          rows="6"
          placeholder="Final content to save"
        />
      </div>

      <!-- Per-row error message, if the last apply failed. -->
      <div
        v-if="currentState.status === 'error' && currentState.errorMessage"
        class="atlas-suggestion-review__error"
      >
        <v-icon size="14" color="error">mdi-alert-circle</v-icon>
        <span>{{ currentState.errorMessage }}</span>
      </div>
    </div>

    <!-- Footer — per-suggestion actions. -->
    <div class="atlas-suggestion-review__footer">
      <template v-if="!isCurrentLocked">
        <button
          type="button"
          class="atlas-suggestion-review__btn atlas-suggestion-review__btn--ghost"
          :disabled="currentState.status === 'saving'"
          @click="skipCurrent"
        >
          Skip
        </button>
        <button
          type="button"
          class="atlas-suggestion-review__btn atlas-suggestion-review__btn--primary"
          :disabled="!canApplyCurrent"
          @click="applyCurrent"
        >
          <v-progress-circular
            v-if="currentState.status === 'saving'"
            indeterminate
            size="14"
            width="2"
            color="white"
          />
          <span v-else>Apply</span>
        </button>
      </template>
      <template v-else>
        <span class="atlas-suggestion-review__status-pill">
          <v-icon
            size="14"
            :color="currentState.status === 'applied' ? 'success' : 'grey'"
          >
            {{ currentState.status === 'applied' ? 'mdi-check-circle' : 'mdi-skip-next-circle' }}
          </v-icon>
          {{ currentState.status === 'applied' ? 'Applied' : 'Skipped' }}
        </span>
        <button
          type="button"
          class="atlas-suggestion-review__btn atlas-suggestion-review__btn--primary"
          @click="finishOrAdvance"
        >
          {{ allResolved ? 'Done' : 'Next' }}
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import { ref, computed, reactive, watch } from 'vue';
import AtlasLogo from './AtlasLogo.vue';
import MarkdownContent from './MarkdownContent.vue';

export default {
    name: 'AtlasSuggestionReview',
    components: { AtlasLogo, MarkdownContent },
    props: {
        /**
         * Array of suggestions from the agent's `document.suggest-content`
         * page_action payload. Each entry describes one document part
         * and the suggested replacement content (free-form + optional
         * structured items the user can toggle).
         *
         * Shape (one entry):
         *   {
         *     doc_part_id: number,
         *     part_label: string,
         *     current_content: string,
         *     items?: Array<{ id, text, included_by_default }>,
         *     suggested_content?: string,  // used when `items` is empty
         *     rationale?: string,
         *   }
         */
        suggestions: { type: Array, required: true },
        /**
         * Async handler invoked when the user clicks Apply on a row.
         * Receives `{ doc_part_id, final_text, suggestion }` and MUST
         * return a Promise that resolves on a successful save (any
         * value is fine) or rejects with an Error to surface the
         * message inline on the row.
         *
         * Function-as-prop instead of `@apply` event because Vue 2
         * event emits don't propagate listener return values — we
         * need the resolved/rejected promise so the wizard can drive
         * its own 'saving' → 'applied' / 'error' status transition.
         */
        applyHandler: { type: Function, required: true },
        /**
         * Plain text (or markdown) of the assistant turn that
         * emitted the page action which opened this review. Shown as
         * a chat-context band above the header so the user has the
         * agent's narration in view alongside the structured
         * suggestions. Null when no triggering message could be
         * found (e.g. dev-console dispatch).
         */
        triggerMessage: { type: String, default: null },
        /**
         * Part groups for which the review can offer a "delete the
         * existing rows first" option — i.e. multi-row parts that
         * already have rows AND have at least one new-row (create)
         * suggestion in this review. The host computes these (it owns
         * the document tree); the wizard renders a per-group toggle on
         * the create cards and, when ticked, asks the host to clear the
         * existing rows before creating the replacements.
         *
         * Shape (one entry):
         *   { tp_id: number, label: string, count: number }
         *     count = how many existing rows would be removed.
         */
        replaceTargets: { type: Array, default: () => [] },
    },
    emits: ['done', 'cancel'],
    setup(props, { emit }) {
        const currentIndex = ref(0);
        const rationaleOpen = ref(true);

        /**
         * One state object per suggestion, kept in lockstep with
         * `props.suggestions`. Built up-front so prev/next preserves
         * the user's choices on previously-visited rows.
         *
         * Plain array of `reactive()` elements rather than `reactive([])`
         * — Vue 2.7's array reactivity can't be tracked at the root in
         * watchers (logs a warning). Each element is its own reactive
         * object, so property mutations (`state.status = 'applied'`,
         * `state.included['i-1'] = false`) still trigger updates.
         */
        const suggestionStates = props.suggestions.map((s) =>
            reactive(buildInitialState(s)),
        );

        function buildInitialState(suggestion) {
            // `included` is a Record<itemId, boolean> rather than a Set
            // — Vue 2.7 can't observe Set.add/.delete, so we use a plain
            // object pre-populated with all known item ids so toggle
            // assignments track reactively.
            const included = {};
            for (const it of suggestion.items || []) {
                included[it.id] = it.included_by_default !== false;
            }
            // Re-seed the applied state from a flag stashed on the
            // suggestion object itself (set in `applyCurrent`). The Atlas
            // right-panel tab is `v-if`'d in Document_Holder, so switching
            // tabs away and back UNMOUNTS this wizard and reruns `setup()`
            // — rebuilding these states from scratch. The suggestion
            // objects, though, live on the host's `pendingSuggestions` and
            // outlive the remount. Without this an already-applied row
            // would come back as 'pending' and could be applied — and
            // saved — a second time (the bug this guards against). We only
            // persist 'applied'; a skipped row is free to be reconsidered
            // on reopen, and that re-show is harmless (nothing was saved).
            const applied = suggestion._reviewStatus === 'applied';
            return {
                included,
                finalText:
                    applied && suggestion._reviewFinalText != null
                        ? suggestion._reviewFinalText
                        : assembleText(suggestion, included),
                status: applied ? 'applied' : 'pending', // 'pending' | 'saving' | 'applied' | 'skipped' | 'error'
                errorMessage: null,
            };
        }

        /**
         * Build the editable `finalText` from the ticked items in
         * declaration order, joined by double newlines so each item
         * reads as a paragraph/bullet. Falls back to the agent's
         * free-form `suggested_content` when there are no structured
         * items (or none ticked, to avoid stranding the user with an
         * empty textarea — they'd need to retick to recover).
         */
        function assembleText(suggestion, includedMap) {
            const items = suggestion.items || [];
            if (items.length === 0) return suggestion.suggested_content || '';
            const picked = items.filter((it) => includedMap[it.id]);
            if (picked.length === 0) return '';
            return picked.map((it) => it.text).join('\n\n');
        }

        const currentSuggestion = computed(
            () => props.suggestions[currentIndex.value],
        );
        const currentState = computed(
            () => suggestionStates[currentIndex.value],
        );

        const isCurrentLocked = computed(() =>
            ['applied', 'skipped'].includes(currentState.value.status),
        );

        const canGoPrev = computed(() => currentIndex.value > 0);
        const canGoNext = computed(
            () => currentIndex.value < props.suggestions.length - 1,
        );
        const canApplyCurrent = computed(() => {
            const s = currentState.value;
            return (
                // An applied/skipped row is locked — never re-appliable.
                !isCurrentLocked.value &&
                s.status !== 'saving' &&
                typeof s.finalText === 'string' &&
                s.finalText.trim().length > 0
            );
        });
        const allResolved = computed(() =>
            suggestionStates.every((s) =>
                ['applied', 'skipped'].includes(s.status),
            ),
        );

        /**
         * Per-part "delete existing rows first" intent, keyed by tp_id so
         * the choice is shared across every new-row card of the same part
         * group. Reassigned wholesale (not mutated) on toggle — Vue 2.7
         * can't observe new keys added to a `ref({})` in place.
         *
         * Transient by design: it's a pre-apply intent, so a tab-switch
         * remount resetting it to "off" is acceptable (the user re-ticks).
         * Once the group's first card is applied the host has already
         * cleared the rows, so re-ticking is a host-side no-op.
         */
        const replaceExisting = ref({});
        function setReplace(tpId, checked) {
            replaceExisting.value = {
                ...replaceExisting.value,
                [tpId]: !!checked,
            };
        }
        // Effective "replace existing rows" state for a part group.
        // Defaults to ON for any group the host flagged as a replace
        // target (the agent set `replace_existing` because the user asked
        // to reduce/combine), unless the user has explicitly toggled it.
        function replaceEnabledFor(tpId) {
            const map = replaceExisting.value;
            if (Object.prototype.hasOwnProperty.call(map, tpId)) {
                return !!map[tpId];
            }
            return props.replaceTargets.some((t) => t.tp_id === tpId);
        }
        // The replace target for the current card. Shown on EVERY card of
        // a flagged group — the agent may express a reduce/combine as
        // updates to existing rows OR as new rows, so the toggle isn't
        // gated on create-vs-update; the host deletes whichever rows the
        // set doesn't cover.
        const currentReplaceTarget = computed(() => {
            const s = currentSuggestion.value;
            if (!s) return null;
            return (
                props.replaceTargets.find((t) => t.tp_id === s.tp_id) || null
            );
        });
        const isCurrentReplaceChecked = computed(() => {
            const s = currentSuggestion.value;
            return !!(s && replaceEnabledFor(s.tp_id));
        });

        // Reset rationale-open state per suggestion so going next on a
        // suggestion without a rationale doesn't leave the toggle in a
        // mismatched state. Default to open the first time the user
        // lands on a row, collapsed if they manually closed it earlier.
        watch(currentIndex, () => {
            rationaleOpen.value = true;
        });

        function toggleItem(itemId, checked) {
            const state = currentState.value;
            if (isCurrentLocked.value) return;
            state.included[itemId] = !!checked;
            // Reassemble the textarea content from the new selection.
            // We intentionally OVERWRITE any prior free-form edits —
            // diff-preserving merges are not worth the complexity here;
            // the user can re-edit after toggling.
            state.finalText = assembleText(
                currentSuggestion.value,
                state.included,
            );
        }

        function goPrev() {
            if (canGoPrev.value) currentIndex.value -= 1;
        }
        function goNext() {
            if (canGoNext.value) currentIndex.value += 1;
        }

        /**
         * Move to the next still-pending suggestion if one exists,
         * otherwise emit `done` so the host can clear the review state.
         * Called after Apply/Skip + after Next on an already-resolved
         * row.
         */
        function finishOrAdvance() {
            const nextPending = suggestionStates.findIndex(
                (s, i) => i > currentIndex.value && s.status === 'pending',
            );
            if (nextPending !== -1) {
                currentIndex.value = nextPending;
                return;
            }
            // No forward pending — try earlier ones (user may have
            // skipped, then changed their mind by going back).
            const earlierPending = suggestionStates.findIndex(
                (s) => s.status === 'pending',
            );
            if (earlierPending !== -1) {
                currentIndex.value = earlierPending;
                return;
            }
            emit('done');
        }

        function skipCurrent() {
            currentState.value.status = 'skipped';
            currentState.value.errorMessage = null;
            finishOrAdvance();
        }

        /**
         * Apply the current suggestion via the host-supplied
         * `applyHandler` prop. Awaits the returned Promise so a
         * server-side failure surfaces inline on the row; on success
         * we lock the row and advance to the next pending suggestion.
         */
        async function applyCurrent() {
            const state = currentState.value;
            const suggestion = currentSuggestion.value;
            // Guard against re-apply (an already applied/skipped row is
            // locked) and against a double-fire while a save is still in
            // flight — `canApplyCurrent` folds in both checks, but keep
            // the explicit guard so no stray call path can slip a second
            // save through and apply the same suggestion twice.
            if (isCurrentLocked.value || !canApplyCurrent.value) return;
            state.status = 'saving';
            state.errorMessage = null;
            try {
                await props.applyHandler({
                    tp_id: suggestion.tp_id,
                    doc_part_id: suggestion.doc_part_id,
                    // Attribute-level targeting is opt-in on the
                    // agent side — present in ~99% of structured
                    // cases, absent for whole-part replacements.
                    tpa_id: suggestion.tpa_id,
                    dpa_id: suggestion.dpa_id,
                    final_text: state.finalText,
                    // When the user kept the "replace existing rows"
                    // option on (a reduce/combine), the host clears the
                    // part's other rows — those not in this set — once per
                    // group before applying. Sent on both update and
                    // create cards; the host gates on multi-row + flag.
                    replace_existing_rows: replaceEnabledFor(suggestion.tp_id),
                    suggestion,
                });
                state.status = 'applied';
                // Persist onto the suggestion object so a remount (Atlas
                // tab is v-if'd) doesn't reset this row to 'pending' and
                // allow a second apply/save. See buildInitialState.
                suggestion._reviewStatus = 'applied';
                suggestion._reviewFinalText = state.finalText;
                finishOrAdvance();
            } catch (err) {
                state.status = 'error';
                state.errorMessage =
                    (err && (err.message || err.toString())) ||
                    'Failed to apply suggestion';
            }
        }

        function onCancel() {
            emit('cancel');
        }

        return {
            currentIndex,
            suggestionStates,
            currentSuggestion,
            currentState,
            rationaleOpen,
            isCurrentLocked,
            canGoPrev,
            canGoNext,
            canApplyCurrent,
            allResolved,
            currentReplaceTarget,
            isCurrentReplaceChecked,
            setReplace,
            toggleItem,
            goPrev,
            goNext,
            skipCurrent,
            applyCurrent,
            finishOrAdvance,
            onCancel,
        };
    },
};
</script>

<style scoped>
.atlas-suggestion-review {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    background: #ffffff;
}

/*
 * Chat-context band — mirrors the bubble style ChatMessage uses for
 * assistant turns, so the review panel reads as a continuation of
 * the conversation rather than a separate surface. Capped height +
 * scroll so a verbose narration doesn't push the actual suggestion
 * controls off the visible area.
 */
.atlas-suggestion-review__trigger {
    flex-shrink: 0;
    display: flex;
    gap: 10px;
    align-items: flex-start;
    padding: 10px 12px;
    border-bottom: 1px solid #e5e7eb;
    background: linear-gradient(
        to bottom,
        rgba(238, 242, 255, 0.55) 0%,
        rgba(255, 255, 255, 0.4) 100%
    );
    max-height: 180px;
    overflow-y: auto;
}
.atlas-suggestion-review__trigger-avatar {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border: 1px solid rgb(199 210 254);
}
.atlas-suggestion-review__trigger-text {
    min-width: 0;
    flex: 1;
    font-size: 13px;
    line-height: 1.5;
    color: rgb(31 41 55);
}

.atlas-suggestion-review__header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-bottom: 1px solid #e5e7eb;
    background: #f9fafb;
}

.atlas-suggestion-review__nav-btn,
.atlas-suggestion-review__close {
    border: 0;
    background: transparent;
    padding: 4px;
    border-radius: 6px;
    color: #6b7280;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
}
.atlas-suggestion-review__nav-btn:hover,
.atlas-suggestion-review__close:hover {
    background: rgba(0, 0, 0, 0.06);
    color: #111827;
}
.atlas-suggestion-review__nav-btn:disabled {
    color: #d1d5db;
    cursor: not-allowed;
    background: transparent;
}

.atlas-suggestion-review__progress {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
}
.atlas-suggestion-review__progress-text {
    font-size: 12px;
    color: #6b7280;
    font-weight: 500;
}
.atlas-suggestion-review__progress-dots {
    display: flex;
    gap: 4px;
}
.atlas-suggestion-review__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #e5e7eb;
    transition: background 0.15s ease, transform 0.15s ease;
}
.atlas-suggestion-review__dot--applied {
    background: #10b981;
}
.atlas-suggestion-review__dot--skipped {
    background: #9ca3af;
}
.atlas-suggestion-review__dot--saving {
    background: #6366f1;
}
.atlas-suggestion-review__dot--error {
    background: #ef4444;
}
.atlas-suggestion-review__dot--active {
    transform: scale(1.3);
    box-shadow: 0 0 0 2px #fff, 0 0 0 3px #6366f1;
}

.atlas-suggestion-review__body {
    flex: 1;
    overflow-y: auto;
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.atlas-suggestion-review__part-label {
    font-size: 15px;
    font-weight: 600;
    color: #111827;
}

.atlas-suggestion-review__rationale {
    background: #f3f4f6;
    border-radius: 6px;
    padding: 8px 10px;
}
.atlas-suggestion-review__rationale-text {
    margin-top: 6px;
    font-size: 12.5px;
    line-height: 1.45;
    color: #374151;
    white-space: pre-wrap;
}

.atlas-suggestion-review__section {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.atlas-suggestion-review__section-label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6b7280;
    display: flex;
    align-items: center;
    gap: 8px;
}
.atlas-suggestion-review__hint {
    text-transform: none;
    font-weight: 400;
    color: #9ca3af;
    letter-spacing: 0;
}

.atlas-suggestion-review__section-toggle {
    border: 0;
    background: transparent;
    padding: 0;
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    color: #374151;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.atlas-suggestion-review__current {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 8px 10px;
    font-size: 13px;
    color: #374151;
    white-space: pre-wrap;
    max-height: 120px;
    overflow-y: auto;
}
.atlas-suggestion-review__empty {
    color: #9ca3af;
}

.atlas-suggestion-review__items {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.atlas-suggestion-review__item-row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 6px 8px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
    color: #1f2937;
    line-height: 1.4;
}
.atlas-suggestion-review__item-row:hover {
    background: rgba(99, 102, 241, 0.06);
}
.atlas-suggestion-review__item-row input[type='checkbox'] {
    margin-top: 3px;
    accent-color: #6366f1;
}
.atlas-suggestion-review__item-text {
    flex: 1;
    white-space: pre-wrap;
}

.atlas-suggestion-review__textarea {
    width: 100%;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    padding: 8px 10px;
    font: inherit;
    font-size: 13px;
    color: #111827;
    resize: vertical;
    min-height: 90px;
    background: #ffffff;
}
.atlas-suggestion-review__textarea:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}
.atlas-suggestion-review__textarea:disabled {
    background: #f3f4f6;
    color: #6b7280;
}

.atlas-suggestion-review__error {
    display: flex;
    align-items: center;
    gap: 6px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 6px;
    padding: 6px 10px;
    font-size: 12.5px;
    color: #991b1b;
}

.atlas-suggestion-review__replace {
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 6px;
    padding: 8px 10px;
}
.atlas-suggestion-review__replace-row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    color: #92400e;
    line-height: 1.4;
}
.atlas-suggestion-review__replace-row input[type='checkbox'] {
    margin-top: 2px;
    accent-color: #d97706;
}
.atlas-suggestion-review__replace-hint {
    margin-top: 4px;
    padding-left: 24px;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.45;
    color: #b45309;
}

.atlas-suggestion-review__footer {
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border-top: 1px solid #e5e7eb;
    background: #fafafa;
}

.atlas-suggestion-review__btn {
    border: 0;
    padding: 7px 14px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
    min-width: 80px;
    min-height: 32px;
}
.atlas-suggestion-review__btn--primary {
    background: #4f46e5;
    color: #ffffff;
}
.atlas-suggestion-review__btn--primary:hover:not(:disabled) {
    background: #4338ca;
}
.atlas-suggestion-review__btn--primary:disabled {
    background: #c7d2fe;
    color: #ffffff;
    cursor: not-allowed;
}
.atlas-suggestion-review__btn--ghost {
    background: transparent;
    color: #4b5563;
    border: 1px solid #d1d5db;
}
.atlas-suggestion-review__btn--ghost:hover:not(:disabled) {
    background: #f3f4f6;
    color: #1f2937;
}
.atlas-suggestion-review__btn--ghost:disabled {
    color: #d1d5db;
    cursor: not-allowed;
}

.atlas-suggestion-review__status-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12.5px;
    font-weight: 600;
    color: #374151;
    margin-right: auto;
}
</style>
